import { Injectable, OnDestroy } from '@angular/core';
import { CONSTANTS } from '../../../Constants/Constants';
import { IReportData } from '../../../models/reports/IReportData';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { TranslateService } from '@ngx-translate/core';
import { CommonDataService } from '../../common-data.service';
import { IndexDBService } from '../../common/indexDB.service';
import { ITableData } from '../../../models/reports/ITableData';
import { Material, MaterialData, SQWebArea, SQWebSystem, SubSystem } from '../../../models/dtos';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { SQWebSystemChildren } from '../../../models/SQWebSystemChildren';
import { IData } from '../../../models/reports/IData';
import { IFooter } from '../../../models/reports/IFooter';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { MaterialIndicators } from '../../../models/MaterialIndicators';
import { roundToPrecision } from '../../../../types/number-extensions';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision-pipe/decimal-precision.pipe';

type ElectricalServiceMaterialType = {
  referenceNumber: string;
  description: string;
  quantity: number;
  esHours: number;
  totalHours: number;
  materialIndicator: MaterialIndicators;
}

@Injectable()
export class ElectricalReportService implements OnDestroy {

  private electricalServiceData: ITableData[] = [];
  private electricalServiceRowData: ElectricalServiceMaterialType[] = [];
  private isElectricalServicesProcessed: boolean = false;
  private totalHoursRow: IFooter[] = [];

  constructor(private readonly commonDataService: CommonDataService,
    private readonly translate: TranslateService,
    private readonly indexDbService: IndexDBService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe) { }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true) {
    this.isElectricalServicesProcessed = false;
    const tableData = await this.generateTableData();
    const reportName = this.reportsCommonService.getSelectedReportName();
    const translatedReportName = this.translate.instant(reportName);
    let reportData: IReportData;
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    reportData = {
      selectionName: translatedReportName,
      reportTitle: translatedReportName,
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: tableData,
      pageOrientation: pageOrientation,
      internalReport: internalReport
    }
    this.reportsCommonService.setSelectedReport(reportData);
  }

  async generateTableData() {
    if (!this.isElectricalServicesProcessed) {
      await this.generateElectricalReportData();
      this.isElectricalServicesProcessed = true;
    }
    return this.electricalServiceData;
  }

  async generateElectricalReportData(): Promise<void> {
    this.electricalServiceRowData = [];
    this.electricalServiceData = [];
    this.totalHoursRow = [];
    const sqWebProject = this.commonDataService.getSQWebProject();
    const allMaterialData = (await this.indexDbService.getAllMaterialData()).materialsCollection;
    if (sqWebProject?.areas) {
      for (const area of sqWebProject.areas) {
        if (area.isItemVisible && area.children.length > 0) {
          this.processElectricalReportData(area, allMaterialData);
        }
      }
    }
    if (this.electricalServiceRowData.length === CONSTANTS.ZERO) {
      return;
    }
    this.electricalServiceData.push(
      {
        title: [
          { data: this.translate.instant('REFERENCE_NUMBER'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
          { data: this.translate.instant('DESCRIPTION'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, width: '220' },
          { data: this.translate.instant('Quantity'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, width: '50' },
          { data: this.translate.instant('UNIT_HOURS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
          { data: this.translate.instant('TOTAL_HOURS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        ],
        data: this.processDataSourceForReport(),
        isPageBreakAfter: false,
        tableFooter: this.totalHoursRow,
        isTableBordered: true
      }
    );
  }

  processDataSourceForReport(): IData[] {
    const reportData: IData[] = [];
    let totalHours: number = 0;
    let totalQuantity: number = 0;
    for (const row of this.electricalServiceRowData) {
      reportData.push({
        data: [
          { data: row.referenceNumber, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, materialIndicator: row.materialIndicator },
          { data: row.description, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE },
          { data: row.quantity.toString(), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
          { data: this.formatHours(row.esHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { data: this.formatHours(row.totalHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT }
        ]
      });
      totalHours += row.totalHours;
      totalQuantity += row.quantity;
    }
    this.totalHoursRow.push({
      headers: [
        { headerText: this.translate.instant('TOTALS'), columnspan: CONSTANTS.ONE },
        { headerText: '', columnspan: CONSTANTS.ONE },
        { headerText: totalQuantity.toString(), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: '', columnspan: CONSTANTS.ONE },
        { headerText: this.formatHours(totalHours), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ]
    });

    reportData.sort((a, b) => {
      const refA = a.data[0].data.toUpperCase();
      const refB = b.data[0].data.toUpperCase();
      return refA.localeCompare(refB);
    });
    return reportData;
  }

  private formatHours(value: number): string {
    return this.decimalPrecisionPipe.transform(roundToPrecision(value, this.commonDataService.getDecimalPrecision()));
  }

  processElectricalReportData(area: SQWebArea, allMaterialData: Record<string, MaterialData>) {
    for (const child of area.children) {
      const childItem = child as SQWebAreaChildren;
      if (childItem.isItemVisible) {
        if (childItem.nodeType === CONSTANTS.NODETYPE_AREA) {
          // Recursively process sub-areas
          this.processElectricalReportData(child as SQWebArea, allMaterialData);
        } else if (childItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
          const system = childItem as SQWebSystem;
          const material = allMaterialData[system.itemUniqueId];
          if (system.isSystemES && material?.systemChildrenCollection) {
            this.processMaterialData(material);
          }
        }
      }
    }
  }

  processMaterialData(material: MaterialData) {
    for (const child of material.systemChildrenCollection) {
      const subSystem = child as SQWebSystemChildren;
      if (subSystem.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        const subSystemItem = subSystem as SubSystem;
        if (subSystemItem.isMaster && subSystemItem.quantity > 0) {
          this.processSubSystem(subSystemItem);
        }
      } else {
        this.processMaterialItem(child as Material);
      }
    }
  }

  processSubSystem(subSystem: SubSystem) {
    for (const child of subSystem.children) {
      const childItem = child as Material;
      this.processMaterialItem(childItem, subSystem.quantity);
    }
  }

  processMaterialItem(materialItem: Material, subSystemQuantity: number = CONSTANTS.ONE) {
    // Only master rows carry the quantity; non-master instances repeat it and inflate the total.
    if (materialItem.isMaster && materialItem.quantity > 0) {
      const existingRow = this.electricalServiceRowData.find(row => row.referenceNumber === materialItem.referenceNumber && row.esHours === materialItem.esHours);
      if (existingRow) {
        existingRow.quantity += materialItem.quantity * subSystemQuantity;
        existingRow.totalHours = existingRow.quantity * existingRow.esHours;
        existingRow.materialIndicator = this.reportsCommonService.generateMaterialIndicator(materialItem) as MaterialIndicators;
      } else {
        this.electricalServiceRowData.push({
          referenceNumber: materialItem.referenceNumber,
          description: materialItem.description,
          quantity: materialItem.quantity * subSystemQuantity,
          esHours: materialItem.esHours,
          totalHours: (materialItem.quantity * subSystemQuantity) * materialItem.esHours,
          materialIndicator: this.reportsCommonService.generateMaterialIndicator(materialItem) as MaterialIndicators
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.electricalServiceData = [];
    this.electricalServiceRowData = [];
    this.totalHoursRow = [];
  }

}
